/**
 * AdvisorDiagnosticTranslation Component
 * 
 * Shows how well the advisor translates diagnostic findings to customers.
 * Catalyst+ feature.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Stethoscope, AlertTriangle, ArrowUpRight, HelpCircle } from 'lucide-react';
import type { AdvisorIntelligenceStats } from '@/hooks/useAdvisorIntelligenceStats';

interface AdvisorDiagnosticTranslationProps {
  stats: AdvisorIntelligenceStats | null | undefined;
  isSelfView: boolean;
}

export function AdvisorDiagnosticTranslation({ stats, isSelfView }: AdvisorDiagnosticTranslationProps) {
  if (!stats) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Stethoscope className="h-5 w-5" />
              Diagnostic Translation
            </CardTitle>
            <Badge variant="secondary">Catalyst</Badge>
          </div>
          <CardDescription>Not enough diagnostic data yet.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const formatRate = (rate: number | null) => {
    if (rate == null) return '—';
    return `${Math.round(rate * 100)}%`;
  };

  const getScoreColor = (score: number | null) => {
    if (score == null) return 'text-muted-foreground';
    if (score >= 75) return 'text-green-600 dark:text-green-400';
    if (score >= 50) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getInsight = () => {
    if (!stats.diagTranslationScore) return null;
    
    if (stats.diagTranslationScore >= 80) {
      return {
        type: 'strength',
        message: 'Strong at communicating diagnostic findings to customers',
        icon: ArrowUpRight,
      };
    }
    
    if (stats.highConfStallRate && stats.highConfStallRate > 0.2) {
      return {
        type: 'growth',
        message: 'High-confidence diagnostics sometimes stall before authorization',
        icon: AlertTriangle,
      };
    }
    
    return null;
  };

  const insight = getInsight();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Stethoscope className="h-5 w-5" />
            Diagnostic Translation
          </CardTitle>
          <Badge variant="secondary">Catalyst</Badge>
        </div>
        <CardDescription>
          How effectively diagnostic findings are communicated and authorized
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Translation Score - managers only */}
        {!isSelfView && stats.diagTranslationScore != null && (
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div>
              <div className="text-sm font-medium">Translation Effectiveness</div>
              <div className="text-xs text-muted-foreground">
                Based on diagnostic confidence vs. authorization outcomes
              </div>
            </div>
            <div className={`text-2xl font-bold ${getScoreColor(stats.diagTranslationScore)}`}>
              {Math.round(stats.diagTranslationScore)}
            </div>
          </div>
        )}

        {/* Detail Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">High-Confidence Stall Rate</span>
              <span className="font-semibold">{formatRate(stats.highConfStallRate)}</span>
            </div>
            <Progress 
              value={stats.highConfStallRate != null ? (1 - stats.highConfStallRate) * 100 : 0} 
              className="h-2"
            />
            <p className="text-xs text-muted-foreground">
              Lower is better — high-confidence diagnostics should authorize quickly
            </p>
          </div>

          <div className="space-y-2 p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Low-Confidence Escalation</span>
              <span className="font-semibold">{formatRate(stats.lowConfEscalationRate)}</span>
            </div>
            <Progress 
              value={stats.lowConfEscalationRate != null ? stats.lowConfEscalationRate * 100 : 0} 
              className="h-2"
            />
            <p className="text-xs text-muted-foreground">
              Uncertain diagnostics that were properly escalated
            </p>
          </div>
        </div>

        {/* Insight */}
        {insight && (
          <div className={`flex items-start gap-3 p-3 rounded-lg ${
            insight.type === 'strength' 
              ? 'bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800'
              : 'bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800'
          }`}>
            <insight.icon className={`h-5 w-5 mt-0.5 ${
              insight.type === 'strength' ? 'text-green-600' : 'text-amber-600'
            }`} />
            <div>
              <div className="text-sm font-medium">
                {insight.type === 'strength' ? 'Strength' : 'Growth Area'}
              </div>
              <p className="text-sm text-muted-foreground">{insight.message}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
